import { useTranslation } from 'react-i18next';

import { Card, CardContent } from '~/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import { cn } from '~/utils/cn';

import type {
  CategoryFilterOption,
  RevenueSource,
  TestVsControlRevenuePoint,
  WeeklyDidRevenuePoint,
} from '../../types/experiment.types';

import { ExperimentTestVsControlBarChart } from './experiment-test-vs-control-bar-chart';
import { ExperimentTrendEmptyState } from './experiment-trend-empty-state';
import { ExperimentWeeklyDidRevenueChart } from './experiment-weekly-did-revenue-chart';
import { RevenueSourceTag } from './revenue-source-tag';

interface Props {
  availableCategories: CategoryFilterOption[];
  selectedCategory: string;
  onCategoryChange: (next: string) => void;
  weeklyPoints: WeeklyDidRevenuePoint[];
  revenuePoints: TestVsControlRevenuePoint[];
  isLoading?: boolean | undefined;
  /** Source of the revenue figures; null/absent renders no tag. */
  revenueSource?: RevenueSource | null | undefined;
}

/**
 * Wrapper card hosting the two ECharts visualizations side-by-side: the
 * "DiD % Revenue — Week by Week" line chart and "Test vs. Control Revenue"
 * grouped bar chart. A category dropdown drives both charts via the parent's
 * controlled state. Stacks below md.
 */
export function ExperimentWeeklyDidRevenueSection({
  availableCategories,
  selectedCategory,
  onCategoryChange,
  weeklyPoints,
  revenuePoints,
  isLoading,
  revenueSource,
}: Props) {
  const { t } = useTranslation('experiments');
  const isEmpty = !isLoading && availableCategories.length === 0;

  return (
    <Card className="min-w-0 overflow-hidden">
      <CardContent className="min-w-0 space-y-6 p-6">
        {/* Header — title + sub-title left, category dropdown right. */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="max-w-3xl">
            <h3 className="flex flex-wrap items-center gap-2 text-[16px] font-medium">
              {t('detail.weeklyDidRevenue.title', 'Weekly DiD Revenue Trend')}
              <RevenueSourceTag source={revenueSource} />
            </h3>
            <p className="text-muted-foreground text-sm">
              {t(
                'detail.weeklyDidRevenue.subtitle',
                'Week-over-week test vs. control performance by category',
              )}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <label
              htmlFor="weekly-did-revenue-category"
              className="text-foreground text-sm font-medium"
            >
              {t('detail.weeklyDidRevenue.category', 'Category:')}
            </label>
            <Select value={selectedCategory} onValueChange={onCategoryChange} disabled={isEmpty}>
              <SelectTrigger
                id="weekly-did-revenue-category"
                className={cn('w-[220px]', isEmpty ? 'cursor-not-allowed' : 'cursor-pointer')}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableCategories.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value} className="cursor-pointer">
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Two charts side-by-side; empty state when no data has arrived yet. */}
        {!isLoading && weeklyPoints.length === 0 && revenuePoints.length === 0 ? (
          <ExperimentTrendEmptyState
            title={t('detail.weeklyDidRevenue.emptyTitle')}
            subtitle={t('detail.weeklyDidRevenue.emptySubtitle')}
          />
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="min-w-0 space-y-2">
              <p className="text-foreground text-sm font-semibold">
                {t('detail.weeklyDidRevenue.lineTitle', 'DiD % Revenue — Week by Week')}
              </p>
              <ExperimentWeeklyDidRevenueChart points={weeklyPoints} isLoading={isLoading} />
            </div>
            <div className="min-w-0 space-y-2">
              <p className="text-foreground text-sm font-semibold">
                {t('detail.weeklyDidRevenue.barTitle', 'Test vs. Control Revenue')}
              </p>
              <ExperimentTestVsControlBarChart points={revenuePoints} isLoading={isLoading} />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
